import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { MoneyDrawer } from "./CryptoTab";

/* ===== Never-Red lab — the user's hypothesis, forward-tested in isolation =====
   Once a trade is +bePct%, the stop floors at cost-covered breakeven (can never
   close red net of fees), then trails trailPct% off the peak. Same 4h pullback
   entries as the proven book, MAJORS only, fully separate module/files/loop.
   The number that settles the argument: SCRATCH RATE vs the 4h book's record. */

const rup = (n) => `${n < 0 ? "−" : "+"}₹${Math.abs(Math.round(n || 0)).toLocaleString("en-IN")}`;
const when = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) : "—";

export default function NeverRed() {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState(null);
  const load = useCallback(async () => {
    try { const r = await axios.get("/api/crypto/neverred/status", { timeout: 20000 }); setSt(r.data); setErr(""); }
    catch (e) { setErr(String(e.message || e)); }
  }, []);
  useEffect(() => { load(); const h = setInterval(load, 15000); return () => clearInterval(h); }, [load]);
  const post = async (p, body) => { try { await axios.post(`/api/crypto/neverred/${p}`, body || {}); } catch {} load(); };
  if (!st) return <div className="dashboard"><p style={{ color: "var(--muted2)" }}>{err || "loading…"}</p></div>;

  const cfg = st.config || {};
  const knob = (k, step) => (
    <label key={k} style={{ fontSize: 11, color: "var(--muted2)" }}>{k}{" "}
      <input type="number" step={step} defaultValue={cfg[k]} onBlur={e => { const v = +e.target.value; if (v !== cfg[k]) post("config", { [k]: v }); }}
        style={{ width: 64, background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 4, padding: "1px 4px" }} />
    </label>
  );

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h2>Never-Red Lab <span style={{ fontSize: 12, color: "var(--muted2)", fontWeight: 400 }}>isolated paper experiment · majors, {cfg.tf || "1h"} {cfg.entryMode === "st" ? "Supertrend-flip" : "pullback"} entries · floors at BE+costs once +{cfg.bePct}% then trails {cfg.trailPct}% off peak</span></h2>
          <p className="dashboard-subtitle" style={{ maxWidth: 980 }}>
            The hypothesis under test: "a trade that goes +{cfg.bePct}% should never be allowed to close red." Entries: {cfg.entryMode === "st" ? `Supertrend(10,3) flip-to-up on ${cfg.tf} candles — a signal the house REFUTED as a raw system (Round 8: 15m ST −15R in 2026); the test is whether never-red management rescues it` : "EMA pullback-reclaim (the main books\u2019 entry)"} —
            so every difference in results is the management. House prediction on record: a high <b>scratch rate</b> (floored exits near ₹0) will eat the big winners.
            This tab exists so the forward test, not the debate, decides. Fully isolated: own module, files and loop — the existing books cannot be affected.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {st.active
            ? <button className="btn btn-sm btn-danger" onClick={() => post("stop")}>Stop</button>
            : <button className="btn btn-sm btn-primary" onClick={() => post("start")}>Start</button>}
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", background: st.active ? "var(--tint-pos)" : "var(--panel3)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "8px 14px", marginBottom: 10, fontSize: 12.5 }}>
        <span>{st.active ? "● RUNNING 24/7" : "○ STOPPED"}</span>
        <span>open {st.openCount} · closed {st.closedCount}</span>
        <span>net <b className={st.netPnl >= 0 ? "positive" : "negative"}>{rup(st.netPnl)}</b> ({st.netR >= 0 ? "+" : ""}{st.netR}R)</span>
        <span title="Closed within ±0.1R — the predicted failure mode of early breakeven floors">
          scratch rate <b className={st.scratchRate > 40 ? "negative" : ""}>{st.scratchRate}%</b> ({st.scratchCount} of {st.closedCount})
        </span>
        <span>real wins {st.winCount}</span>
        <span title="Both legs at the audited 0.0472%/side — already inside every P&L figure">fees ₹{Math.round(st.totalFees || 0).toLocaleString("en-IN")}</span>
        <span title="Modeled at 0.01%/8h on notional at Shark's stamps — inside P&L; note: a long-held 'floored' trade can still close slightly red from funding">funding ₹{Math.round(st.totalFunding || 0).toLocaleString("en-IN")}</span>
        {st.lastError && <span className="negative">err: {st.lastError}</span>}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)" }}>Knobs (hot)</span>
        {knob("bePct", 0.1)}{knob("costPct", 0.05)}{knob("trailPct", 0.1)}{knob("riskPerTrade", 500)}
        <label style={{ fontSize: 11, color: "var(--muted2)" }}>tf{" "}
          <select value={cfg.tf || "1h"} onChange={e => post("config", { tf: e.target.value })}
            style={{ background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 4, padding: "1px 4px", fontSize: 11 }}>
            {["15m", "1h", "4h"].map(x => <option key={x} value={x}>{x}</option>)}
          </select></label>
        <label style={{ fontSize: 11, color: "var(--muted2)" }}>entry{" "}
          <select value={cfg.entryMode || "pullback"} onChange={e => post("config", { entryMode: e.target.value })}
            style={{ background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 4, padding: "1px 4px", fontSize: 11 }}>
            <option value="st">ST(10,3) flip</option>
            <option value="pullback">EMA pullback</option>
          </select></label>
      </div>

      <div className="tl-table-wrap">
        <table className="stocks-table" style={{ width: "100%" }}>
          <thead><tr><th>Ref</th><th>Symbol</th><th>Opened</th><th>Entry</th><th>LTP / Exit px</th><th>Stop</th><th>Peak</th><th>State</th><th>Exit</th><th title="entry+exit legs">Fees</th><th>Funding</th><th>Net P&L</th><th>R</th></tr></thead>
          <tbody>
            {[...(st.openTrades || []), ...[...(st.closedTrades || [])].reverse()].map(t => (
              <tr key={t.id} className={t.status === "OPEN" ? "trade-open-row" : ""} onClick={() => setDetail(t)} style={{ cursor: "pointer" }} title="Click for full details">
                <td>{t.ref}</td>
                <td style={{ fontWeight: 600 }}>{t.symbol}</td>
                <td style={{ whiteSpace: "nowrap" }}>{when(t.entry_time)}</td>
                <td>{t.entry}</td>
                <td>{t.status === "OPEN" ? (t.lastPrice ?? "…") : <b>{t.exit_price}</b>}</td>
                <td className="sl">{t.stop}{t.floored && <span style={{ fontSize: 9.5, color: "#22c55e" }}> 🔒NR</span>}</td>
                <td>{t.peak}</td>
                <td style={{ fontSize: 11 }}>{t.status === "OPEN" ? (t.floored ? "never-red, trailing" : "full risk, floor unarmed") : (t.exit_reason === "FLOOR" ? "exit: never-red floor hit" : "exit: structural stop")}</td>
                <td style={{ whiteSpace: "nowrap" }}>{when(t.exit_time)}</td>
                <td style={{ color: "var(--muted2)" }}>₹{Math.round((t.entryFee || 0) + (t.exitFee || 0))}</td>
                <td style={{ color: "var(--muted2)" }}>₹{Math.round(t.fundingFee || 0)}</td>
                <td className={(t.pnl_inr || 0) >= 0 ? "positive" : "negative"}>{t.pnl_inr != null ? rup(t.pnl_inr) : "—"}</td>
                <td className={(t.r_mult || 0) >= 0 ? "positive" : "negative"}>{t.r_mult != null ? `${t.r_mult >= 0 ? "+" : ""}${t.r_mult}R` : "—"}</td>
              </tr>
            ))}
            {!(st.openTrades || []).length && !(st.closedTrades || []).length &&
              <tr><td colSpan={13} style={{ color: "var(--muted2)" }}>no trades yet — entries fire on the next qualifying pullback on BTC/ETH/SOL/BNB/XRP</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 10, color: "var(--faint2)", marginTop: 8, lineHeight: 1.6 }}>
        Judgement criteria (set in advance, 05 Oct 2026): after ≥30 closes, compare net R and scratch rate against the 4h ST-trail book over the same period.
        If never-red wins, the floor gets considered for the main book at the evidence-chosen trigger; if the scratch bleed shows up as predicted, the lab retires with the receipt.
        Exits: STOP = structural stop before floor armed (normal 1R loss) · FLOOR = never-red stop after arming (≥ ₹0 net by construction).
      </div>

      {detail && (() => {
        const t = { quote: "USDT", fxEntry: 95.6, remainingQty: detail.qty, bookedPnl: 0,
                    side: "LONG", exit_reason: detail.exit_reason, ...detail };
        const charges = (t.entryFee || 0) + (t.exitFee || 0) + (t.fundingFee || 0);
        const live = t.status === "OPEN" && t.lastPrice != null
          ? (t.lastPrice - t.entry) * t.qty * 95.6 - charges : null;
        const lev = t.riskInr && t.notional ? Math.max(1, Math.floor(t.notional / (1.5 * t.riskInr))) : 10;
        return <MoneyDrawer t={t} charges={charges} live={live} lev={lev}
                 cfg={st.config || {}} params={{}} onClose={() => setDetail(null)} />;
      })()}
    </div>
  );
}
